import React, { useEffect, useRef } from 'react';
import EmployeeSidebar from './EmployeeSidebar';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import ThemeToggle from '../common/ThemeToggle';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sun, LogOut, User } from 'lucide-react';

const EmployeeLayout = ({ children }) => {
  const { logout, userName, userRole, companyName } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef(null);

  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  // Reset scroll of layout container on route change
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
      mainRef.current.scrollLeft = 0;
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className={theme === 'dark' ? 'dark' : ''}>
      <div className="flex h-screen bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-100 font-sans transition-colors duration-200">
        <EmployeeSidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="bg-white/80 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800/80 backdrop-blur-md px-6 py-4 flex justify-between items-center z-10 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#10B981] to-[#047857] flex items-center justify-center shadow-lg shadow-[#10B981]/10">
                <Sun className="w-4 h-4 text-white animate-pulse" />
              </div>
              <span className="text-md sm:text-lg font-black text-slate-900 dark:text-white uppercase tracking-widest">
                {companyName || "SolarPro"}
              </span>
            </div>

            <div className="flex items-center gap-4 sm:gap-6">
              {/* Theme Toggle Button */}
              <ThemeToggle />

              {/* Logged in User info */}
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center text-[#10B981]">
                  <User className="w-4 h-4" />
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-extrabold text-slate-900 dark:text-white leading-none">
                    {userName || "Employee"}
                  </p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest mt-1">
                    {userRole || "Staff"}
                  </p>
                </div>
              </div>

              {/* Logout Trigger */}
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 text-xs sm:text-sm font-bold transition-all duration-200 px-3 py-2 rounded-xl bg-red-500/5 hover:bg-red-500/10 border border-red-500/10 cursor-pointer"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </header>

          {/* Content Section with scroll ref */}
          <main ref={mainRef} className="flex-1 overflow-y-auto p-6 bg-slate-50 dark:bg-slate-950 transition-colors">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default EmployeeLayout;